import React, { useState, useEffect } from 'react';
import { FiSearch, FiX, FiCalendar, FiUser, FiHome, FiMapPin, FiPhone, FiMail, FiCheckCircle, FiXCircle, FiClock, FiEye, FiChevronLeft, FiChevronRight, FiDownload } from 'react-icons/fi';
import { FaRupeeSign } from "react-icons/fa";
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RoomManagement = () => {
  const [roomUsers, setRoomUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [vendors, setVendors] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    checkedIn: 0,
    bookingsToday: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchRoomUsers();
    fetchVendors();
  }, [statusFilter, vendorFilter, search, currentPage, itemsPerPage]);

  const fetchRoomUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (statusFilter !== 'all') params.status = statusFilter;
      if (vendorFilter !== 'all') params.vendorId = vendorFilter;
      if (search) params.search = search;

      const response = await api.get('/superadmin/room-users', { params });
      
      setRoomUsers(response.data.data || []);
      setTotalUsers(response.data.total || 0);
      setTotalPages(response.data.totalPages || 1);
      
      // Set statistics from API response
      if (response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching room users:', error);
      toast.error('Failed to fetch room users');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/superadmin/vendors?approvalStatus=approved&limit=100');
      setVendors(response.data.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'checked_in': return 'bg-green-100 text-green-800';
      case 'checked_out': return 'bg-gray-100 text-gray-800';
      case 'booked': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = async () => {
    try {
      toast.loading('Exporting data...');
      
      // Get all data for export (no pagination)
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (vendorFilter !== 'all') params.vendorId = vendorFilter;
      if (search) params.search = search;
      
      const response = await api.get('/superadmin/room-users', { 
        params: { ...params, page: 1, limit: 10000 } 
      });
      
      const allUsers = response.data.data || [];
      
      if (allUsers.length === 0) {
        toast.dismiss();
        toast.error('No data to export');
        return;
      }
      
      // Prepare CSV data
      const headers = [
        'Guest Name',
        'Mobile Number',
        'Email',
        'Vendor Name',
        'Room Number',
        'Room Type',
        'Floor',
        'Check-in Date/Time',
        'Status',
        'Room Amount',
        'GST Amount',
        'Other Amount',
        'Final Amount',
        'Tip Amount',
        'Discount Amount',
        'Final Paid Amount',
        'Payment Method'
      ];
      
      const csvData = allUsers.map(user => [
        user.persons && user.persons[0] ? user.persons[0].name : 'N/A',
        user.persons && user.persons[0] ? user.persons[0].mobileNumber : 'N/A',
        user.persons && user.persons[0] ? user.persons[0].email : 'N/A',
        user.vendorId?.restaurantDetails?.restaurantName || 'N/A',
        user.roomId?.roomNumber || 'N/A',
        user.roomId?.type || 'N/A',
        user.floorId?.name || 'N/A',
        formatDate(user.checkinDateTime),
        user.status?.replace('_', ' ').toUpperCase() || 'N/A',
        user.roomAmount || 0,
        user.gstAmount || 0,
        user.otherAmount || 0,
        user.finalAmount || 0,
        user.tipAmount || 0,
        user.discountAmount || 0,
        user.finalPaidAmount || 0,
        user.paymentMethod || 'N/A'
      ]);
      
      // Convert to CSV
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `room-users-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.dismiss();
      toast.success(`Exported ${allUsers.length} records successfully`);
      
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export data');
      console.error('Export error:', error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading && roomUsers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Room Management</h1>
        <p className="text-gray-600">View all users who have used room services across all vendors</p>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, mobile..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          
          {/* Status Filter */}
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          >
            <option value="all">All Status</option>
            <option value="checked_in">Checked In</option>
            <option value="checked_out">Checked Out</option>
            <option value="booked">Booked</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Vendor Filter */}
          <select 
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
          >
            <option value="all">All Vendors</option>
            {vendors.map((vendor) => (
              <option key={vendor._id} value={vendor._id}>
                {vendor.restaurantDetails?.restaurantName || 'Unknown Vendor'}
              </option>
            ))}
          </select>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiDownload className="text-lg" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiUser className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Currently Checked In</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.checkedIn}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FiCheckCircle className="text-green-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Bookings Today</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.bookingsToday}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiCalendar className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-purple-600">
                ₹{stats.totalRevenue.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <FaRupeeSign className="text-purple-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Details</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-in</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {roomUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <FiHome className="text-gray-400 text-4xl mb-4" />
                      <p className="text-gray-500 font-medium">No room users found</p>
                      <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                roomUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {user.persons && user.persons[0] ? user.persons[0].name : 'N/A'}
                        </p>
                        <p className="text-sm text-gray-500">{user.persons && user.persons[0] ? user.persons[0].mobileNumber : 'N/A'}</p>
                        <p className="text-sm text-gray-500">{user.persons && user.persons[0] ? user.persons[0].email : 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">
                        {user.vendorId?.restaurantDetails?.restaurantName || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">{user.vendorId?.personalDetails?.email || 'N/A'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FiHome className="text-gray-400 mr-2" />
                        <div>
                          <p className="font-medium text-gray-900">{user.roomId?.roomNumber || 'N/A'}</p>
                          <p className="text-sm text-gray-500">{user.roomId?.type || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FiCalendar className="text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm text-gray-900">{formatDate(user.checkinDateTime)}</p>
                          {user.actualCheckoutDateTime && (
                            <p className="text-sm text-gray-500">{formatDate(user.actualCheckoutDateTime)}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                        {user.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FaRupeeSign className="text-gray-400 mr-1" />
                        <span className="font-medium text-gray-900">{(user.finalAmount || 0).toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm space-y-2">
                        {/* Room Amount */}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Room:</span>
                          <span className="font-medium">₹{(user.roomAmount || 0).toLocaleString()}</span>
                        </div>
                        
                        {/* GST Amount */}
                        {(user.gstAmount || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">GST:</span>
                            <span className="font-medium">₹{(user.gstAmount || 0).toLocaleString()}</span>
                          </div>
                        )}
                        
                        {/* Other Amount */}
                        {(user.otherAmount || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Other:</span>
                            <span className="font-medium">₹{(user.otherAmount || 0).toLocaleString()}</span>
                          </div>
                        )}
                        
                        {/* Final Amount */}
                        <div className="flex justify-between font-semibold border-t border-gray-200 pt-1">
                          <span className="text-gray-800">Final:</span>
                          <span className="text-gray-900">₹{(user.finalAmount || 0).toLocaleString()}</span>
                        </div>
                        
                        {/* Tip Amount */}
                        {(user.tipAmount || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-green-600">Tip:</span>
                            <span className="font-medium text-green-600">₹{user.tipAmount.toLocaleString()}</span>
                          </div>
                        )}
                        
                        {/* Discount Amount */}
                        {(user.discountAmount || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-orange-600">Discount:</span>
                            <span className="font-medium text-orange-600">₹{user.discountAmount.toLocaleString()}</span>
                          </div>
                        )}
                        
                        {/* Final Paid Amount */}
                        <div className="flex justify-between font-semibold border-t border-gray-200 pt-1">
                          <span className="text-gray-800">Paid:</span>
                          <span className="text-gray-900">₹{(user.finalPaidAmount || 0).toLocaleString()}</span>
                        </div>
                        
                        {/* Payment Method */}
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Method:</span>
                          <span className="text-gray-600 capitalize">{user.paymentMethod || 'N/A'}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft />
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomManagement;
